import axios from "axios";
import { useState, useMemo, useRef } from "react";
import { Modal, Form, Button, Spinner } from "react-bootstrap";
import { Edit, Download } from "lucide-react";
import useAuthAdmin from "../../../../../hooks/Admin/useAuthAdmin";
import { useNavigate, useParams } from "react-router-dom";
import { handleApiError } from "../../../../../utils/handleApiError";

export default function DocumentModal({
  showModal,
  handleClose,
  documentType,
  refetch,
  documents,
}) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  const { id } = useParams();

  const BASE_URL = "https://docs.guardsoft.co.uk/";

  // Check if document exists for this type
  const existingDocument = useMemo(() => {
    if (!documents || !documentType) return null;
    
    // Documents is an object with type keys (e.g., { address: [...], creditcheck: [...] })
    const typeDocs = documents[documentType];
    
    if (typeDocs && Array.isArray(typeDocs) && typeDocs.length > 0) {
      return typeDocs[typeDocs.length - 1];
    }
    return null;
  }, [documents, documentType]);

  // Get document URL for download
  const documentUrl = useMemo(() => {
    if (existingDocument && existingDocument.documentUrl) {
      return BASE_URL + existingDocument.documentUrl;
    }
    return null;
  }, [existingDocument]);

  // Check if existing document looks like an image (by extension)
  const isImageDocument = useMemo(() => {
    if (!existingDocument || !existingDocument.documentUrl) return false;
    const lower = existingDocument.documentUrl.toLowerCase();
    return (
      lower.endsWith(".jpg") ||
      lower.endsWith(".jpeg") ||
      lower.endsWith(".png") ||
      lower.endsWith(".gif") ||
      lower.endsWith(".webp")
    );
  }, [existingDocument]);

  // Get document title for download - use documentType as fallback
  const documentTitle = useMemo(() => {
    // You can enhance this by passing documentItems or title as prop if needed
    return documentType || "document";
  }, [documentType]);

  // Show Edit and Download buttons when document exists
  const showEditOption = existingDocument !== null;
  const showDownloadOption = existingDocument !== null && documentUrl !== null;

  const allowedTypes = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/jpg",
  ];
  const { adminSession, logout } = useAuthAdmin();
  const navigate = useNavigate();
  const headers = {
    Authorization: `Bearer ${adminSession}`,
  };
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    const validFiles = selectedFiles.filter((file) =>
      allowedTypes.includes(file.type)
    );

    if (validFiles.length !== selectedFiles.length) {
      setError("Only PDF, JPG, and PNG files are allowed.");
    } else {
      setError("");
    }

    // Merge new files with existing ones (without duplicates by name)
    const fileNames = files.map((f) => f.name);
    const newUniqueFiles = validFiles.filter(
      (f) => !fileNames.includes(f.name)
    );

      setFiles([...files, ...newUniqueFiles]);

    // Reset file input
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleEditFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check if file type is valid
      if (!allowedTypes.includes(selectedFile.type)) {
        setError("Only PDF, JPG, and PNG files are allowed.");
        return;
      }

      setError("");
      setIsEditMode(true);
      // Just add file to list, don't auto-upload
      setFiles([selectedFile]);
    }

    // Reset file input
    if (e.target) {
      e.target.value = '';
    }
  };


  const handleEditClick = () => {
    if (editFileInputRef.current) {
      editFileInputRef.current.click();
    }
  };

  const handleRemoveFile = (fileName) => {
    const updatedFiles = files.filter((file) => file.name !== fileName);
    setFiles(updatedFiles);
  };

  const handleSubmit = async (isEdit = false) => {
    if (files.length === 0) {
      setError("Please select at least one valid file to upload.");
      return;
    }

    setUploading(true);
    setError("");

    try {
      // Upload new document(s) (for edit we rely on backend to keep old or replace;
      // UI always shows latest, so no need to delete explicitly here)
      const formData = new FormData();
      files.forEach((file) => {
        formData.append("documents", file);
      });

      const createRes = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/employees/documents/${id}/${documentType}`,
        formData,
        {
          headers: {
            ...headers,
          },
        }
      );

      if (createRes.status === 200) {
        setFiles([]);
        setIsEditMode(false);
        refetch();
        handleClose();
      } else {
        throw new Error("Upload failed");
      }
    } catch (err) {
      handleApiError(err, logout, navigate);
      setError(isEdit ? "Failed to update document. Please try again." : "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      size="md"
      centered
      className="w-50-modal"
    >
      <Modal.Header>
        <Modal.Title>
          <h5 className="mb-0">
            Upload Documents ({documentType}) {id}
          </h5>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          {/* Hidden file input for Edit button */}
          <input
            type="file"
            ref={editFileInputRef}
            style={{ display: "none" }}
            onChange={handleEditFileChange}
            accept=".pdf,image/*"
          />

          {/* Show existing image preview when document already uploaded */}
          {showEditOption && isImageDocument && documentUrl && (
            <div className="mb-4 text-center">
              <img
                src={`${documentUrl}?download=2`}
                alt={`Preview of ${documentTitle}`}
                style={{ maxWidth: "100%", maxHeight: "350px", objectFit: "contain" }}
              />
            </div>
          )}

          <Form.Group controlId="formFileMultiple" className="mb-4">
            <Form.Label className="fw-semibold">Choose Documents</Form.Label>
            {/* For already uploaded documents, hide the normal chooser and rely on Edit button */}
            {!showEditOption && (
              <Form.Control
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".pdf,image/*"
              />
            )}
            {error && (
              <small className="text-danger d-block mt-2">{error}</small>
            )}

            {files.length > 0 && (
              <div className="mt-3">
                <strong>Selected files:</strong>
                <ul className="list-unstyled mt-2">
                  {files.map((file, index) => (
                    <li
                      key={index}
                      className="d-flex align-items-center justify-content-between bg-light p-2 rounded mb-2"
                    >
                      <div className="text-success d-flex align-items-center">
                        <i className="bi bi-file-earmark-arrow-up-fill me-2"></i>
                        {file.name}
                      </div>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemoveFile(file.name)}
                      >
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Form.Group>

          <Form.Text muted>
            Accepted formats: PDF, JPG, PNG. Max size per file: 10MB.
          </Form.Text>
        </Form>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <div className="d-flex gap-2">
          {showDownloadOption && (
            <a
              href={`${documentUrl}?download=1`}
              download={documentTitle}
              className="btn-action btn-download"
              style={{
                padding: "0.6rem 1.2rem",
                borderRadius: "8px",
                border: "none",
                fontSize: "1rem",
                fontWeight: "500",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                backgroundColor: "#007bff",
                color: "white",
                textDecoration: "none",
                transition: "background-color 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#0056b3";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#007bff";
              }}
            >
              <Download size={18} /> Download
            </a>
          )}
          {showEditOption && (
            <button
              className="btn-action btn-edit"
              onClick={handleEditClick}
              disabled={uploading}
              style={{
                padding: "0.6rem 1.2rem",
                borderRadius: "8px",
                border: "none",
                fontSize: "1rem",
                fontWeight: "500",
                cursor: uploading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                backgroundColor: "#ffc107",
                color: "#212529",
                transition: "background-color 0.2s ease",
              }}
              onMouseEnter={(e) => {
                if (!uploading) e.target.style.backgroundColor = "#e0a800";
              }}
              onMouseLeave={(e) => {
                if (!uploading) e.target.style.backgroundColor = "#ffc107";
              }}
            >
              {uploading ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Updating...
                </>
              ) : (
                <>
                  <Edit size={18} /> Edit
                </>
              )}
            </button>
          )}
          <Button 
            variant="primary" 
            onClick={() => handleSubmit(isEditMode)} 
            disabled={uploading}
          >
            {uploading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {isEditMode ? "Updating..." : "Uploading..."}
              </>
            ) : (
              isEditMode ? "Update Document" : "Upload Documents"
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
